import { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Icon, Loader, Modal, Pagination, Text, tokens } from '@hotjar/hotsauce';
import { useCurrentSite, useCurrentSiteId } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import type { Collection } from '@hotjar/webapp/domains/collections/model';
import { HighlightsGuide } from '@hotjar/webapp/features/Activation/SpaceOverview/components/GuideCards';
import {
  DEFAULT_CURRENT_PAGE,
  useHighlightsPagination,
} from '@hotjar/webapp/features/Highlights/List/use-highlights-pagination';
import { trackCollection } from '@hotjar/webapp/domains/collections/api';
import { AddCollection } from '@hotjar/webapp/features/Collections/AddCollection';
import { createCollectionLink } from '@hotjar/webapp/features/Collections/CollectionsSidebar/utils';
import { StandaloneShortLinkDropdown } from '@hotjar/webapp/features/ShortLink/StandaloneShortLinkDropdown';
import {
  EmptyStateKind,
  FeatureEmptyState,
} from '@hotjar/webapp/features/Activation/FeatureEmptyState';

import { useHighlightsListPerformance } from './use-highlights-list-performance';
import { Filters } from './Filters';
import { Card } from './Card';
import { HighlightModal } from './HighlightModal';
import { HighlightsEmptyState } from './HighlightsEmptyState';
import { HighlightsHeader } from './HighlightsHeader';
import { useFilteredHighlightsList } from './use-filtered-highlights-list';
import { useUnseenHighlightsReset } from './use-unseen-highlights-reset';
import { useKeybindings } from './use-keybindings';
import { DeleteModal } from '../SingleHighlight/DeleteModal';
import { useTrackHighlightFiltersEffect } from './utils/track-filter-helper';

const HighlightsContainer = styled.section`
  padding: ${tokens.spacer.x3};
  background-color: ${tokens.BRAND_COLORS.white};
`;

const HighlightShortLinkDropdown = styled(StandaloneShortLinkDropdown)`
  position: absolute;
  z-index: 9999999;
`;

const HighlightsListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: ${tokens.spacer.x3};
`;

const ShareModal = styled(Modal)`
  width: fit-content;
  height: fit-content;
  padding: 32px;
  min-height: auto;

  .ReactModal__Content {
    width: fit-content;
    height: fit-content;
    padding: 32px;
    min-height: auto;
  }
`;

const FeedbackLink = styled.a`
  ${tokens.text('body2')}
  color: ${tokens.color.text_light};
  display: inline-flex;
  text-decoration: underline;
  margin-bottom: ${tokens.spacer.x4};
`;

const FeedbackIcon = styled(Icon)`
  margin-left: ${tokens.spacer.x0pt5};
`;

const Title = styled(Text)`
  display: block;
  padding-bottom: ${tokens.spacer.x0pt5};
`;

const StyledHighlightsCount = styled(Text)`
  margin-bottom: ${tokens.spacer.x2};
`;

const HighlightsCount = styled.span`
  color: ${tokens.color.text_light};
`;

const StyledLoader = styled(Loader)`
  min-height: 80vh;
`;

const PaginationBox = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${tokens.spacer.x3};
`;

const NewCollectionButton = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

const EmptyStateWrapper = styled.div`
  padding: ${tokens.spacer.x6pt5} 80px;
`;

interface HighlightsListProps {
  collection: Collection;
  isEmptyStateExperimentEnabled: boolean;
  collectionsStatus: 'idle' | 'error' | 'loading' | 'success';
}

export const HighlightsList = ({
  collection,
  collectionsStatus,
  isEmptyStateExperimentEnabled,
}: HighlightsListProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentSiteId = useCurrentSiteId();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const [selectedHighlightId, setSelectedHighlightId] = useState<string | null>(null);
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = useHighlightsPagination(
    collection.id,
  );
  const resetPage = useCallback(() => setCurrentPage(DEFAULT_CURRENT_PAGE), [setCurrentPage]);

  const {
    data: highlightsList,
    status,
    filters,
  } = useFilteredHighlightsList(
    collection.id,
    {
      currentPage,
      itemsPerPage,
    },
    resetPage,
  );

  const selectedHighlightIndex = highlightsList?.items.findIndex(
    (highlight) => highlight.id === selectedHighlightId,
  );

  const { showNext, showPrevious, handleKeyDown } = useKeybindings(
    selectedHighlightIndex ?? null,
    setSelectedHighlightId,
    highlightsList,
  );

  const [dropdownActionHighlightId, setDropdownActionHighlightId] = useState<string | null>(null);

  const [isShortLinkWidgetShown, setIsShortLinkWidgetShown] = useState(false);

  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);

  useEffect(() => {
    if (currentSiteId) {
      trackCollection(currentSiteId, collection.id);
    }
  }, [currentSiteId, collection]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const canBeMeasured =
    typeof highlightsList !== 'undefined' &&
    typeof currentSite !== 'undefined' &&
    currentSiteId !== null;

  useHighlightsListPerformance(canBeMeasured);

  useTrackHighlightFiltersEffect({
    siteId: currentSite?.id.toString() ?? '',
    organizationId: currentSite?.organizationId.toString() ?? '',
    accountId: currentSite?.account.id.toString() ?? '',
    collectionName: collection.name,
  });

  useUnseenHighlightsReset(currentSite?.id?.toString() ?? null);

  if (status === 'error') {
    return <div>{t('highlights:list.serverError')}</div>;
  }

  if (isEmptyStateExperimentEnabled) {
    return (
      <EmptyStateWrapper>
        <FeatureEmptyState kind={EmptyStateKind.HIGHLIGHTS} />
      </EmptyStateWrapper>
    );
  }

  if (
    typeof highlightsList === 'undefined' ||
    typeof currentSite === 'undefined' ||
    currentSiteId === null
  ) {
    return (
      <HighlightsContainer>
        <Title as="h3" textType="header3">
          {collection.keyName
            ? t(collection.keyName)
            : collection.name ?? t('highlights:list.title')}
        </Title>
        <StyledLoader size="xl" />
      </HighlightsContainer>
    );
  }

  const searchParams = new URLSearchParams(window.location.search);
  const highlightSearchParam = searchParams.get('highlight');
  if (highlightSearchParam && !selectedHighlightId) {
    setSelectedHighlightId(highlightSearchParam);
  }

  const isTheListEmpty =
    collection.highlightsCount === 0 && collectionsStatus === 'success' && status === 'success';

  const currentOrganizationId = String(currentSite.organization.id);

  const onCreateCollectionSuccess = (newCollectionId: string): void => {
    if (currentSiteId) {
      navigate(createCollectionLink(currentSiteId, newCollectionId));
    }
  };

  return (
    <HighlightsContainer onKeyDown={handleKeyDown}>
      {currentSiteId !== null && (
        <AddCollection
          isOpen={isCreateModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={onCreateCollectionSuccess}
          siteId={currentSiteId}
        />
      )}
      <HighlightsHeader
        collection={collection}
        newCollectionButton={
          <NewCollectionButton
            kind="secondary"
            size="s"
            icon="plus"
            onClick={() => {
              setCreateModalOpen(true);
            }}
          >
            {t('collections:actions.new')}
          </NewCollectionButton>
        }
      />
      <FeedbackLink
        rel="noreferrer"
        href="https://surveys.hotjar.com/39ade915-baad-4ad3-97c2-713562f9968e"
        target="_blank"
      >
        {t('highlights:list.feedback')}
        <FeedbackIcon icon="open_new_window" />
      </FeedbackLink>
      {isTheListEmpty && <HighlightsGuide />}
      {!isTheListEmpty && (
        <Filters {...filters.state} {...filters.actions} siteId={currentSiteId} />
      )}
      <StyledHighlightsCount as="p" textType="subtitle1">
        <Trans
          i18nKey="highlights:list.highlightsCount"
          count={highlightsList.totalCount}
          values={{ allHighlights: collection.highlightsCount, count: highlightsList.totalCount }}
        >
          <HighlightsCount />
        </Trans>
      </StyledHighlightsCount>
      {isTheListEmpty && <HighlightsEmptyState siteId={currentSiteId} />}
      <HighlightsListContainer data-testid="highlights-list-container">
        {!isTheListEmpty
          ? highlightsList.items?.map((highlight) => (
              <Card
                currentUserId={currentUser?.id}
                key={highlight.id}
                highlight={highlight}
                setSelectedHighlightId={(id: string) => setSelectedHighlightId(id)}
                setDropdownHighlightId={setDropdownActionHighlightId}
                setIsShortLinkWidgetShown={setIsShortLinkWidgetShown}
                siteId={currentSiteId}
                startRemoval={() => setIsDeleteConfirmationVisible(true)}
              />
            ))
          : null}
      </HighlightsListContainer>
      {!isTheListEmpty ? (
        <PaginationBox>
          <Pagination
            totalCount={highlightsList.totalCount}
            currentPage={currentPage}
            withSpacer
            pageSize={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={setItemsPerPage}
          />
        </PaginationBox>
      ) : null}
      <HighlightModal
        selectedHighlightId={selectedHighlightId}
        currentUser={currentUser}
        organizationId={currentOrganizationId}
        siteId={currentSiteId}
        onClose={() => setSelectedHighlightId(null)}
        currentHighlightIndex={selectedHighlightIndex ?? null}
        highlightsCount={highlightsList.items.length}
        showPrevious={() => showPrevious(selectedHighlightIndex ?? null)}
        showNext={() => showNext(selectedHighlightIndex ?? null)}
      />
      {isShortLinkWidgetShown && dropdownActionHighlightId && (
        <ShareModal
          isOpen={isShortLinkWidgetShown && !!dropdownActionHighlightId}
          onClose={() => {
            setIsShortLinkWidgetShown(false);
            setDropdownActionHighlightId(null);
          }}
          hideCloseButton
          contentLabel={t('common:actions.share')}
        >
          <HighlightShortLinkDropdown
            uiPosition="highlight_card"
            highlightId={dropdownActionHighlightId}
          />
        </ShareModal>
      )}

      {dropdownActionHighlightId && (
        <DeleteModal
          siteId={currentSiteId}
          highlightId={dropdownActionHighlightId}
          onModalClose={() => {
            setDropdownActionHighlightId(null);
            setIsDeleteConfirmationVisible(false);
          }}
          isOpen={isDeleteConfirmationVisible}
          uiPosition="highlight_card"
        />
      )}
    </HighlightsContainer>
  );
};
